/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex, jsx-a11y/role-supports-aria-props */
import React, {
	FC,
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import './MultiView.css';

export interface MultiViewRequest {
	title: string;
	content: ReactNode;
	ariaLabel?: string;
}

interface ActiveMultiViewRequest extends MultiViewRequest {
	instanceId: number;
}

type CloseGuard = (() => boolean) | null;

interface MultiViewContextValue {
	isOpen: boolean;
	openMultiView: (request: MultiViewRequest) => boolean;
	closeMultiView: () => boolean;
	setCloseGuard: (guard: CloseGuard) => void;
}

const defaultMultiViewContext: MultiViewContextValue = {
	isOpen: false,
	openMultiView: () => false,
	closeMultiView: () => false,
	setCloseGuard: () => undefined,
};
const MultiViewContext = createContext<MultiViewContextValue>(defaultMultiViewContext);

export const useMultiView = (): MultiViewContextValue => useContext(MultiViewContext);

export const useMultiViewCloseGuard = (guard: CloseGuard): void => {
	const value = useContext(MultiViewContext);
	const setCloseGuard = value?.setCloseGuard;
	useEffect(() => {
		if (!setCloseGuard) return undefined;
		setCloseGuard(guard);
		return () => setCloseGuard(null);
	}, [guard, setCloseGuard]);
};

export const MultiViewProvider: FC = ({ children }) => {
	const [request, setRequest] = useState<ActiveMultiViewRequest | null>(null);
	const nextInstanceId = useRef(0);
	const guardRef = useRef<CloseGuard>(null);

	const setCloseGuard = useCallback((guard: CloseGuard) => {
		guardRef.current = guard;
	}, []);

	const canClose = useCallback(() => !guardRef.current || guardRef.current(), []);
	const closeMultiView = useCallback(() => {
		if (!canClose()) return false;
		guardRef.current = null;
		setRequest(null);
		return true;
	}, [canClose]);
	const openMultiView = useCallback(
		(next: MultiViewRequest) => {
			if (request && !canClose()) return false;
			guardRef.current = null;
			nextInstanceId.current += 1;
			setRequest({ ...next, instanceId: nextInstanceId.current });
			return true;
		},
		[canClose, request],
	);
	const value = useMemo(
		() => ({
			isOpen: Boolean(request),
			openMultiView,
			closeMultiView,
			setCloseGuard,
		}),
		[closeMultiView, openMultiView, request, setCloseGuard],
	);

	return (
		<MultiViewContext.Provider value={value}>
			<div className="multi-view-provider" data-multi-view-open={request ? 'true' : 'false'}>
				<MultiViewRequestContext.Provider value={request}>
					{children}
				</MultiViewRequestContext.Provider>
			</div>
		</MultiViewContext.Provider>
	);
};

const MultiViewRequestContext = createContext<ActiveMultiViewRequest | null>(null);

export const getMultiViewShellHeight = (viewportHeight: number, top: number) =>
	Math.max(0, Math.round(Math.max(0, viewportHeight) - Math.max(0, top)));

export const MultiViewHost: FC = ({ children }) => {
	const request = useContext(MultiViewRequestContext);
	const { closeMultiView } = useMultiView();
	const rootRef = useRef<HTMLDivElement>(null);
	const [leftPercent, setLeftPercent] = useState(50);
	const [shellHeight, setShellHeight] = useState(0);
	const draggingRef = useRef(false);

	useEffect(() => {
		if (request) setLeftPercent(50);
	}, [request]);
	useLayoutEffect(() => {
		if (!request) return undefined;
		let frame = 0;
		const { visualViewport } = window as any;
		const measureHeight = () => {
			const top = Math.max(0, rootRef.current?.getBoundingClientRect().top || 0);
			setShellHeight(getMultiViewShellHeight(window.innerHeight, top));
		};
		const queueHeightUpdate = () => {
			window.cancelAnimationFrame(frame);
			frame = window.requestAnimationFrame(() => {
				measureHeight();
			});
		};
		measureHeight();
		window.addEventListener('resize', queueHeightUpdate);
		if (visualViewport) visualViewport.addEventListener('resize', queueHeightUpdate);
		return () => {
			window.cancelAnimationFrame(frame);
			window.removeEventListener('resize', queueHeightUpdate);
			if (visualViewport) visualViewport.removeEventListener('resize', queueHeightUpdate);
		};
	}, [request]);

	const setFromClientX = useCallback((clientX: number) => {
		const rect = rootRef.current?.getBoundingClientRect();
		if (!rect || rect.width <= 0) return;
		const minimumPercent = Math.min(45, (320 / rect.width) * 100);
		const next = ((clientX - rect.left) / rect.width) * 100;
		setLeftPercent(Math.max(minimumPercent, Math.min(100 - minimumPercent, next)));
	}, []);

	useEffect(() => {
		const move = (event: PointerEvent) => {
			if (!draggingRef.current) return;
			setFromClientX(event.clientX);
		};
		const end = () => {
			draggingRef.current = false;
			document.body.classList.remove('multi-view-is-resizing');
		};
		window.addEventListener('pointermove', move);
		window.addEventListener('pointerup', end);
		window.addEventListener('pointercancel', end);
		return () => {
			window.removeEventListener('pointermove', move);
			window.removeEventListener('pointerup', end);
			window.removeEventListener('pointercancel', end);
			document.body.classList.remove('multi-view-is-resizing');
		};
	}, [setFromClientX]);

	if (!request) return <>{children}</>;

	const keyboardResize = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
		event.preventDefault();
		const direction = event.key === 'ArrowLeft' ? -1 : 1;
		setLeftPercent((current) => Math.max(20, Math.min(80, current + direction * 2)));
	};

	return (
		<div className="multi-view-shell" ref={rootRef} style={{ height: `${shellHeight}px` }}>
			<section
				className="multi-view-shell__source"
				aria-label="Source workspace"
				style={{ flexBasis: `${leftPercent}%` }}
			>
				{children}
			</section>
			<div
				className="multi-view-shell__separator"
				role="separator"
				tabIndex={0}
				aria-label="Resize multi-view panes"
				aria-orientation="vertical"
				aria-valuemin={20}
				aria-valuemax={80}
				aria-valuenow={Math.round(leftPercent)}
				onKeyDown={keyboardResize}
				onPointerDown={(event) => {
					event.preventDefault();
					draggingRef.current = true;
					document.body.classList.add('multi-view-is-resizing');
					setFromClientX(event.clientX);
				}}
			>
				<span aria-hidden="true" />
			</div>
			<section
				className="multi-view-shell__review"
				aria-label={request.ariaLabel || request.title}
				style={{ flexBasis: `${100 - leftPercent}%` }}
			>
				<header className="multi-view-shell__header">
					<strong>{request.title}</strong>
					<button type="button" onClick={closeMultiView} aria-label="Close multi-view">
						Close
					</button>
				</header>
				<div className="multi-view-shell__content" key={request.instanceId}>
					{request.content}
				</div>
			</section>
		</div>
	);
};
